/**
 * Simple event bus for decoupled communication between components
 */

import type { GameEventType, GameEvent, EventCallback } from './types';

class EventBus {
  private listeners: Map<GameEventType, Set<EventCallback>> = new Map();

  /**
   * Subscribe to an event type
   */
  on(eventType: GameEventType, callback: EventCallback): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(callback);

    // Return unsubscribe function
    return () => this.off(eventType, callback);
  }

  /**
   * Unsubscribe from an event type
   */
  off(eventType: GameEventType, callback: EventCallback): void {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  /**
   * Emit an event to all subscribers
   */
  emit(eventType: GameEventType, payload?: unknown): void {
    const event: GameEvent = {
      type: eventType,
      payload,
      timestamp: Date.now(),
    };

    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(event);
        } catch (error) {
          console.error(`Error in event handler for ${eventType}:`, error);
        }
      });
    }
  }

  /**
   * Subscribe to an event type for a single emission
   */
  once(eventType: GameEventType, callback: EventCallback): () => void {
    const wrappedCallback: EventCallback = (event) => {
      this.off(eventType, wrappedCallback);
      callback(event);
    };
    return this.on(eventType, wrappedCallback);
  }

  /**
   * Remove all listeners for an event type, or all listeners if no type specified
   */
  clear(eventType?: GameEventType): void {
    if (eventType) {
      this.listeners.delete(eventType);
    } else {
      this.listeners.clear();
    }
  }
}

// Export singleton instance
export const eventBus = new EventBus();
